import React, { useEffect, useState, useMemo, createRef } from "react";
import { DataTable } from "primereact/datatable";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview";
import useGlobalEntitiesTable from "./useGlobalEntitiesTable";
import { useDispatch, useSelector } from "react-redux";
import { fetchGlobalEntityDetails } from "../global-entities/globalEntitiesSlice";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { Link, useNavigate } from "react-router-dom";
import NoEntityAvailable from "./NoEntityAvailable";
import API_ROUTES from "../../api-routes";
import AuditSidebar from "../audit-sidebar/AuditSidebar";
import "./globalentities.scss";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import AdminOnly from "../AdminOnly";
import { Skeleton } from "primereact/skeleton";
import FilterDropdown from "../FilterDropdown";
import { Chip } from "primereact/chip";

const GlobalEntitiesTable = ({
  childEntities = false,
  childEntitiesData = [],
}) => {
  const dispatch = useDispatch();
  const [isAuditSidebarOpen, setIsAuditSidebarOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [childEntity, setChildEntity] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const contextMenu = createRef();
  const { isAdmin } = useSelector((state) => state.login);
  const [selectedValues, setSelectedValues] = useState({});
  const [chipData, setChipData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    country: [],
    entity_type: [],
    account_manager: [],
  });

  const [loading, setLoading] = useState(true);
  const allEntities = useSelector((state) => state.globalEntities.entities);
  const generalStatus = useSelector((state) => state.globalEntities.status);
  const error = useSelector((state) => state.globalEntities.error);

  useEffect(() => {
    if (!childEntities) {
      setLoading(true);
      dispatch(fetchGlobalEntityDetails())
        .then((data) => {
          setFilteredEntities(data.payload || []);
          setLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [dispatch, childEntities]);

  useEffect(() => {
    applyFilters();
  }, [allEntities, childEntities, searchQuery]);

  const { first, rows, setFirst, setRows, onPageChange, handleDownloadCSV } =
    useGlobalEntitiesTable();

  const auditLabels = (data) => {
    return {
      create: data.parent ? "Entity Created" : "Parent Company Created",
      update: data.parent ? "Entity Updated" : "Parent Company Updated",
      delete: data.parent ? "Entity Deleted" : "Parent Company Deleted",
    };
  };

  const auditNameLink = (data) => `/entity/${data.original_pk}/dashboard`;

  const handleCountryFlag = (rowData) => {
    return (
      <div className="country-tag">
        <ReactCountryFlag countryCode={rowData.country} svg />
        <span style={{ textTransform: "capitalize", marginLeft: "7px" }}>
          {getName(rowData.country)}
        </span>
      </div>
    );
  };

  const filterOptions = useMemo(() => {
    let countries;
    let types;
    let accountManagers;
    if (!childEntities) {
      countries = Array.from(
        new Set(allEntities?.map((entity) => entity.country)),
      );
      types = Array.from(new Set(allEntities?.map((entity) => entity.type)));
      accountManagers = Array.from(
        new Set(allEntities?.map((entity) => entity.account_manager_name)),
      );
    } else {
      countries = Array.from(
        new Set(childEntitiesData?.map((entity) => entity.country)),
      );
      types = Array.from(
        new Set(childEntitiesData?.map((entity) => entity.type)),
      );
      accountManagers = Array.from(
        new Set(
          childEntitiesData?.map((entity) => entity.account_manager_name),
        ),
      );
    }
    return [
      {
        name: "Country",
        id: "country",
        suboptions: countries.map((country) => ({
          name: getName(country),
          code: country,
          icon: () => (
            <ReactCountryFlag
              countryCode={country}
              svg
              style={{ marginRight: "8px", width: "1em", height: "1em" }}
            />
          ),
        })),
      },
      {
        name: "Entity type",
        id: "entity_type",
        suboptions: types.map((type) => ({ name: type, code: type })),
      },
      {
        name: "Account Manager",
        id: "account_manager",
        suboptions: accountManagers.map((manager) => ({
          name: manager,
          code: manager,
        })),
      },
    ];
  }, [allEntities]);

  const search = (entityData) => {
    return entityData.filter((entity) => {
      const entityName = entity.name?.toLowerCase() || "";
      const parentEntityName = entity.parentEntity?.name?.toLowerCase() || "";
      const entityType = entity.type?.toLowerCase() || "";
      const countryName = getName(entity.country)?.toLowerCase() || "";
      const accountManagerName =
        entity.account_manager_name?.toLowerCase() || "";
      return (
        entityName.includes(searchQuery) ||
        parentEntityName.includes(searchQuery) ||
        entityType.includes(searchQuery) ||
        countryName.includes(searchQuery) ||
        accountManagerName.includes(searchQuery)
      );
    });
  };
  const handleChipData = (selectedValues) => {
    let chipArray = [];
    for (const key in selectedValues) {
      selectedValues[key].map((data) => {
        chipArray.push({ category: key, value: data });
      });
    }
    setChipData(chipArray);
  };

  const applyFilters = () => {
    let filtered;

    if (!childEntities) {
      filtered = allEntities;
    } else {
      filtered = childEntitiesData;
    }

    if (selectedValues?.country?.length > 0) {
      filtered = filtered.filter((entity) =>
        selectedValues.country.includes(entity.country),
      );
    }

    if (selectedValues?.entity_type?.length > 0) {
      filtered = filtered.filter((entity) =>
        selectedValues.entity_type.includes(entity.type),
      );
    }

    if (selectedValues.account_manager?.length > 0) {
      filtered = filtered.filter((entity) =>
        selectedValues.account_manager.includes(entity.account_manager_name),
      );
    }
    setChildEntity(filtered);

    if (searchQuery) {
      filtered = search(filtered);
    }
    setFilteredEntities(filtered);
    handleChipData(selectedValues);
  };

  const deSelectAll = () => {
    setSelectedFilters({
      country: [],
      entity_type: [],
      account_manager: [],
    });
    let entity;
    if (childEntities) {
      entity = childEntitiesData;
    } else {
      entity = allEntities;
    }
    if (searchQuery) {
      entity = search(entity);
    }
    setChildEntity(entity);
    setFilteredEntities(entity);
    setSelectedValues({});
    setChipData([]);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const redirectToDashboardIfSingleEntity = () => {
    if (!isAdmin && allEntities.length === 1) {
      navigate(`/entity/${allEntities[0].id}/dashboard`);
    }
  };

  useEffect(() => {
    redirectToDashboardIfSingleEntity();
  }, [allEntities]);

  // if (generalStatus === "loading") {
  //   return <p>Loading...</p>;
  // }

  if (generalStatus === "failed") {
    return <p>Error: {error}</p>;
  }

  const handleOnboardNewEntity = () => {
    navigate("/entity/onboard");
  };

  const handleRemoveFilter = (data) => {
    const array = selectedValues[data.category].filter((value) => {
      return value !== data?.value;
    });

    delete selectedFilters[data?.value];
    setSelectedFilters(selectedFilters);
    const filteredValue = {
      ...selectedValues,
      [data.category]: array,
    };
    handleChipData(filteredValue);
    setSelectedValues(filteredValue);
    let filtered;
    if (!childEntities) {
      filtered = allEntities;
    } else {
      filtered = childEntitiesData;
    }
    if (filteredValue?.country?.length > 0) {
      filtered = filtered.filter((entity) =>
        filteredValue.country.includes(entity.country),
      );
    }
    if (filteredValue?.entity_type?.length > 0) {
      filtered = filtered.filter((entity) =>
        filteredValue.entity_type.includes(entity.type),
      );
    }
    if (filteredValue.account_manager?.length > 0) {
      filtered = filtered.filter((entity) =>
        filteredValue.account_manager.includes(entity.account_manager_name),
      );
    }
    setChildEntity(filtered);
    if (searchQuery) {
      filtered = search(filtered);
    }
    setFilteredEntities(filtered);
  };

  return (
    <>
      <div className="formgrid grid justify-content-between pl-2 pr-2">
        <div className="flex w-full justify-content-between">
          <div className="flex-1">
            <div className="input-wrapper-container">
              <span className="pi pi-search"></span>
              <input
                type="text"
                placeholder="Search Entity by Name, Parent Entity, Type or Country"
                className=" appearance-none outline-none w-full"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="pi pi-search"></span>
            </div>
          </div>
          <div>
            <div className="flex align-items-center justify-content-end gap-2">
              <FilterDropdown
                groupedOptions={filterOptions}
                filterCount={chipData?.length}
                value={selectedFilters}
                onChange={(e) => {
                  setSelectedValues(e.grouped);
                  setSelectedFilters(e.value);
                }}
                applyFilter={applyFilters}
                deSelectAll={deSelectAll}
                placeholder="All events"
              />
              <AdminOnly>
                <button
                  className="btn onboard-entity"
                  type="button"
                  onClick={handleOnboardNewEntity}
                >
                  <span>Onboard Entity</span>
                </button>
                <Menu
                  className="global-entities-context-menu"
                  popup
                  ref={contextMenu}
                  id="context_menu"
                  model={[
                    {
                      label: (
                        <div className="global-entities-context-menu-item">
                          Download As CSV
                          <i className="pi pi-download" />
                        </div>
                      ),
                      command: () => handleDownloadCSV(filteredEntities),
                    },
                    {
                      label: (
                        <div className="global-entities-context-menu-item">
                          View History
                          <i className="pi pi-history" />
                        </div>
                      ),
                      command: () => setIsAuditSidebarOpen(true),
                    },
                  ]}
                />
                <Button
                  onClick={(event) => contextMenu.current.toggle(event)}
                  icon="pi pi-ellipsis-v"
                  outlined
                  severity="success"
                  className="menu__toggle"
                />
              </AdminOnly>
            </div>
          </div>
        </div>
        <div className="filter-chips-container">
          {chipData?.length ? `Filters (${chipData?.length})` : null}
          {chipData?.map((data) => (
            <Chip
              key={`${data?.category}-${data?.value}`}
              label={
                <span
                  key={`${data?.category}-${data?.value}`}
                  className="filter__chip"
                >
                  {data?.category === "country" ? (
                    <span>
                      <ReactCountryFlag countryCode={data?.value} svg />
                      {getName(data?.value)}
                    </span>
                  ) : (
                    data?.value
                  )}{" "}
                  <span className="cross__icon">
                    <i
                      className="pi pi-times"
                      onClick={() => handleRemoveFilter(data)}
                    />
                  </span>
                </span>
              }
              removable={false}
            />
          ))}
        </div>
        <Card
          className={`${childEntities ? "child-card" : "gen-card"} h-full w-full mt-4`}
        >
          <div className="field">
            <div className="card">
              {loading ? (
                <DataTable
                  value={Array(10).fill({})}
                  tableStyle={{ minWidth: "100%" }}
                  className="datatable-right-align"
                  paginator
                  rows={rows}
                  rowsPerPageOptions={[10, 20, 30]}
                >
                  <Column
                    header="Registered Name"
                    sortable
                    body={<Skeleton />}
                  />
                  <Column header="Type" sortable body={<Skeleton />} />
                  <Column header="Country" sortable body={<Skeleton />} />
                  <Column header="Parent Entity" sortable body={<Skeleton />} />
                  <Column />
                  <Column
                    header="Account Manager"
                    sortable
                    body={<Skeleton />}
                  />
                </DataTable>
              ) : filteredEntities?.length > 0 ? (
                <DataTable
                  value={
                    childEntities
                      ? [...filteredEntities].sort((a, b) =>
                          a.name.localeCompare(b.name),
                        )
                      : activeIndex === 0
                        ? [...filteredEntities].sort((a, b) =>
                            a.name.localeCompare(b.name),
                          )
                        : null
                    //  filteredEntities
                    //     .filter((item) => item.parent === null)
                    //     .sort((a, b) => a.name.localeCompare(b.name))
                  }
                  sortMode="multiple"
                  tableStyle={{ minWidth: "50rem" }}
                  paginator={childEntity?.length > 10}
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                  rowsPerPageOptions={[10, 20, 30]}
                  className="datatable-right-align"
                  emptyMessage="No results were found"
                  first={first}
                  rows={rows}
                  onPage={onPageChange}
                >
                  <Column
                    field="name"
                    header="Registered Name"
                    sortable
                    body={(rowData) => (
                      <Link
                        className="entity-link"
                        to={`/entity/${rowData.id}/dashboard`}
                      >
                        {rowData.name}
                      </Link>
                    )}
                  />
                  <Column field="type" header="Type" sortable />
                  <Column
                    header="Country"
                    sortable
                    field="country"
                    body={handleCountryFlag}
                  />

                  {!childEntities && activeIndex === 0 && (
                    <Column
                      field="parentEntity.name"
                      header="Parent Entity"
                      sortable
                      body={(rowData) => (
                        <Link
                          to={`/entity/${rowData.parentEntity?.id}/dashboard`}
                          className="entity-link"
                        >
                          {rowData.parentEntity?.name}
                        </Link>
                      )}
                    />
                  )}
                  <Column
                    field="account_manager_name"
                    header="Account Manager"
                    sortable
                    body={(rowData) => rowData?.account_manager_name}
                  />
                </DataTable>
              ) : (
                <NoEntityAvailable />
              )}
            </div>
          </div>
        </Card>
      </div>
      {isAuditSidebarOpen && (
        <AuditSidebar
          heading="My Entities"
          endpoint={API_ROUTES.FETCH_ENTITIES_AUDIT}
          setSidebarToggle={setIsAuditSidebarOpen}
          sidebarToggle={isAuditSidebarOpen}
          labels={auditLabels}
          nameLink={auditNameLink}
        />
      )}
    </>
  );
};

export default GlobalEntitiesTable;
